import type {
  APIComponentAttributes,
  APIModelAttributes,
  APIReferenceAttributes,
  APIReferenceType,
  APIWorkspaceAttributes,
  ArdoqId,
} from '@ardoq/api-types';
import { VIRTUAL_MODEL_ID } from '@ardoq/global-consts';
import { modelOperations } from '@ardoq/core';
import { getReferenceTypesWithDefaults } from '@ardoq/data-model';
import { fallbackModel } from 'models/fallbackModel';

export type DeletionEntities = {
  componentsById: Record<ArdoqId, APIComponentAttributes>;
  referencesById: Record<ArdoqId, APIReferenceAttributes>;
  workspacesById: Record<ArdoqId, APIWorkspaceAttributes>;
  modelsById: Record<ArdoqId, APIModelAttributes>;
};

const getComponentName = (
  { componentsById }: DeletionEntities,
  componentId: ArdoqId
): string | null => componentsById[componentId]?.name ?? null;

const getWorkspaceReferenceTypes = (
  { workspacesById, modelsById }: DeletionEntities,
  workspaceId: ArdoqId
): APIReferenceType[] => {
  const workspace = workspacesById[workspaceId];
  if (!workspace) return [];
  return getReferenceTypesWithDefaults(
    modelsById[workspace.componentModel] ?? fallbackModel
  );
};

const getReferenceType = (
  entities: DeletionEntities,
  { model, type, rootWorkspace }: APIReferenceAttributes
): APIReferenceType | null => {
  if (model === VIRTUAL_MODEL_ID) {
    return entities.modelsById[VIRTUAL_MODEL_ID]?.referenceTypes[type] ?? null;
  }
  return modelOperations.getModelType(
    getWorkspaceReferenceTypes(entities, rootWorkspace),
    type
  );
};

const getShortReferenceName = (
  entities: DeletionEntities,
  { source, target }: APIReferenceAttributes
): string => {
  const sourceName = getComponentName(entities, source) ?? 'Unknown';
  const targetName = getComponentName(entities, target) ?? 'Unknown';
  return `${sourceName} -> ${targetName}`;
};

const getReferenceDisplayName = (
  entities: DeletionEntities,
  referenceId: ArdoqId
): string => {
  const reference = entities.referencesById[referenceId];
  if (!reference) return '';
  const referenceType = getReferenceType(entities, reference);
  const sourceName = getComponentName(entities, reference.source);
  const targetName = getComponentName(entities, reference.target);
  if (!(referenceType && sourceName && targetName)) {
    return getShortReferenceName(entities, reference);
  }
  return `${sourceName} → ${referenceType.name} → ${targetName}`;
};

export const getReferenceDisplayNames = (
  entities: DeletionEntities,
  referenceIds: ArdoqId[]
): string[] =>
  referenceIds.map(referenceId =>
    getReferenceDisplayName(entities, referenceId)
  );

export const getComponentDisplayNames = (
  entities: DeletionEntities,
  componentIds: ArdoqId[]
): string[] =>
  componentIds.map(
    componentId => getComponentName(entities, componentId) || ''
  );
